<template>
    <template v-if="layers.length > 0">
        <Button
            class="legend-trigger"
            severity="secondary"
            size="small"
            icon="pi pi-list"
            label="Layers"
            aria-haspopup="true"
            aria-controls="layer_legend_popover"
            @click="togglePopover"
        />
        <Popover ref="popoverRef" id="layer_legend_popover">
            <div class="layer-pills">
                <button
                    v-for="layer in layers"
                    :key="layer.type"
                    class="layer-pill"
                    :class="{ active: layer.visible }"
                    :style="
                        layer.visible
                            ? {
                                  backgroundColor: layer.color,
                                  borderColor: layer.color,
                              }
                            : {}
                    "
                    @click="$emit('toggle', layer.type, !layer.visible)"
                >
                    <font-awesome-icon
                        v-if="layer.icon"
                        class="layer-icon"
                        :icon="layer.icon"
                        :style="layer.visible ? {} : { color: layer.color }"
                    />
                    <span>{{ layer.label }}</span>
                </button>
            </div>
        </Popover>
    </template>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import Popover from "primevue/popover";
import { ref } from "vue";

interface Layer {
    type: string;
    label: string;
    color: string;
    icon?: string | string[];
    visible: boolean;
}

defineProps<{
    layers: Layer[];
}>();

defineEmits<{
    toggle: [type: string, visible: boolean];
}>();

const popoverRef = ref<InstanceType<typeof Popover>>();
const togglePopover = (event: Event) => {
    popoverRef.value?.toggle(event);
};
</script>

<style scoped lang="scss">
.legend-trigger {
    text-decoration: none;
}

.layer-pills {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    min-width: 12rem;
}

.layer-pill {
    width: 100%;
    padding: 3px 10px;
    border: 1px solid var(--p-content-border-color);
    background: transparent;
    color: var(--p-text-muted-color);
    text-transform: uppercase;
    font-size: 12px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    transition:
        background-color 0.15s ease,
        color 0.15s ease,
        border-color 0.15s ease;

    &:hover {
        background: var(--surface-background-mild);
        color: var(--p-content-color);
    }

    &.active {
        color: rgba(255, 255, 255, 0.75);

        &:hover {
            opacity: 0.9;
        }
    }
}

.layer-icon {
    font-size: 0.95rem;
}
</style>

<style lang="scss">
/* Unscoped: Popover teleports to body, so the scoped attribute can't reach it.
   Targeted by id to avoid affecting other Popover instances. */
#layer_legend_popover {
    margin-block-start: 0;

    &.p-popover::before,
    &.p-popover::after {
        display: none;
    }
}
</style>
